import { CARD_W, CARD_H, CARD_RADIUS } from "./forgeWikiLayouts";

/**
 * One card, drawn into a canvas.
 * ---------------------------------------------------------------------------
 * The GPU surface needs a picture of each card, and the card is a DOM element
 * described by a stylesheet - so the only question worth asking here is how
 * closely the picture has to follow it. The answer is: exactly, because the
 * two are shown side by side. Switching renderer should change how the scene
 * is DRAWN and nothing about what is on it, and a card that loses its
 * repository line or changes type size on the way to the GPU makes the switch
 * feel like a different page rather than a different renderer.
 *
 * So every measure below is taken from .forge-wiki-card in ForgeWiki.css - the
 * 280x124 box, the 14px padding, the 10px gaps, the 14px name over the 10.5px
 * repository, the uppercase 10px goal with its letter-spacing - and every
 * colour is READ from the document rather than written down again. The tints
 * are CSS variables that change with the theme and with whichever palette is
 * in use; a copy of them here would be wrong by the end of the week.
 *
 * What is deliberately not drawn: the hover sweep (it belongs to a pointer,
 * and the pointer is on the canvas), and the controls on an opened card. A
 * card being read closely is a real DOM card laid over the scene, because at
 * twelve times life size a texture is mush and because its buttons have to be
 * clickable. This draws the other two hundred and seventy.
 * ---------------------------------------------------------------------------
 */

/* The document's own colours, read once per batch rather than per card.
   -------------------------------------------------------------------------
   `getComputedStyle` is a layout read, and doing it three hundred times in a
   paint is the difference between a redraw nobody notices and a visible
   stall. The tokens live on .forge-page (not on :root - see Forge.css), so a
   probe wearing that class is what they are read off. */
let inkCache = null;

export function forgetCardInk() { inkCache = null; tints.clear(); }

/**
 * Resolve a stack of CSS values to what they actually paint as.
 *
 * Reading a custom property gives back its TEXT - "color-mix(in srgb, rgb(245,
 * 239, 230) 8%, var(--forge-surface))" - which is a colour to a browser and a
 * string to a canvas. So each one is put on a probe's `color` and read back
 * off the computed style, which is where the cascade has already done the
 * mixing. One probe, one layout read, ten answers.
 */
function resolve(host, values) {
  const probe = document.createElement("span");
  probe.style.cssText = "position:absolute;width:0;height:0;opacity:0;pointer-events:none";
  host.appendChild(probe);
  const out = values.map(v => {
    probe.style.color = "";
    probe.style.color = v;
    return getComputedStyle(probe).color;
  });
  probe.remove();
  return out;
}

function ink() {
  if (inkCache) return inkCache;

  /* The tints live on .forge-wiki-stage and the type tokens on .forge-page
     (see ForgeWiki.css and Forge.css) - neither is on :root, so the stage is
     what both are read through. */
  const host = document.querySelector(".forge-wiki-stage")
    || document.querySelector(".forge-page")
    || document.body;
  const seen = getComputedStyle(host);
  const read = (name, fallback) => (seen.getPropertyValue(name) || "").trim() || fallback;

  const tints = resolve(host, Array.from({ length: 10 }, (unused, i) => `var(--wiki-tint-${i + 1})`));
  const [text, faint, accent, sep, ground] = resolve(host, [
    "var(--forge-text, #f5efe6)",
    "var(--forge-text-30, rgba(245,239,230,0.3))",
    "var(--forge-accent-text, #ff5e19)",
    "var(--forge-sep, rgba(245,239,230,0.14))",
    /* what a card is painted ON, which is what decides what its own weight
       actually comes to and therefore which ink reads on it */
    "var(--forge-page-bg, #060606)",
  ]);
  const sevs = resolve(host, SEVERITIES.map(k => `var(--sev-${k}, #888)`));

  /* What a pane is made of, when the cards are glass: the lit edge, the
     hairline it makes, the lip along the top and the streak off the corner.
     Defined on the stage per theme - see "or a pane of glass" in
     ForgeWiki.css - so these are the same colours the DOM cards wear. */
  const [glassEdge, glassRim, glassLip, glassSheen, glassFloor] = resolve(host, [
    "var(--glass-edge, rgb(164, 226, 205))",
    "var(--glass-rim, rgba(198, 240, 222, 0.42))",
    "var(--glass-lip, rgba(255, 255, 255, 0.34))",
    "var(--glass-sheen, rgba(255, 255, 255, 0.17))",
    "var(--glass-floor, rgba(164, 226, 205, 0.1))",
  ]);

  inkCache = {
    text, faint, accent, sep, tints, ground,
    glassEdge, glassRim, glassLip, glassSheen, glassFloor,
    sevs: Object.fromEntries(SEVERITIES.map((k, i) => [k, sevs[i]])),
    display: read("--forge-font-display", "Inter, sans-serif"),
    mono: read("--forge-font-mono", "ui-monospace, Menlo, monospace"),
  };
  return inkCache;
}

/* The card's mark.
   -------------------------------------------------------------------------
   The DOM card has an <img> in the corner of its head - the language, the
   operating system, the document - and a face without it is recognisably a
   different card. There is no element to lean on here, so the image is loaded
   once per source and kept: a hundred cards on a shelf are a dozen distinct
   marks between them, and the cache is what stops that being a hundred
   requests.

   A face drawn before its mark has arrived is drawn again when it does, which
   is what `onReady` is for - one redraw per image, not per card. */
const marks = new Map();

export function markFor(src, onReady) {
  if (!src) return null;
  const had = marks.get(src);
  if (had) return had.complete && had.naturalWidth ? had : null;

  const img = new Image();
  img.decoding = "async";
  img.onload = () => onReady?.();
  img.onerror = () => { /* a mark that will not load is a card without one */ };
  img.src = src;
  marks.set(src, img);
  return null;
}

/* The four a card always shows, worst first. */
export const SEVERITIES = ["critical", "high", "medium", "low"];

/* ---------------------------------------------------- what reads on what */

/**
 * Any colour CSS hands back, as numbers.
 *
 * Two shapes come out of getComputedStyle depending on what went in: the usual
 * `rgb(r, g, b)` for a plain value, and `color(srgb 0.05 0.05 0.05)` for
 * anything that went through `color-mix`. Both are read here, because a card
 * painted solid is the second kind and a card painted with alpha is the first.
 */
export function readColor(value) {
  return rgb(value);
}

function rgb(value) {
  const said = String(value || "").trim();

  const srgb = said.match(/^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?/i);
  if (srgb) {
    return {
      r: Number(srgb[1]) * 255, g: Number(srgb[2]) * 255, b: Number(srgb[3]) * 255,
      a: srgb[4] === undefined ? 1 : Number(srgb[4]),
    };
  }

  const dec = said.match(/^rgba?\(([^)]+)\)/i);
  if (dec) {
    const n = dec[1].split(/[\s,/]+/).filter(Boolean).map(Number);
    return { r: n[0] || 0, g: n[1] || 0, b: n[2] || 0, a: n[3] === undefined ? 1 : n[3] };
  }

  const hex = said.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    const h = hex[1].length < 6
      ? hex[1].split("").map(c => c + c).join("")
      : hex[1];
    return {
      r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16),
      a: h.length >= 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
    };
  }
  return null;
}

const mix = (over, under, a) => ({
  r: over.r * a + under.r * (1 - a),
  g: over.g * a + under.g * (1 - a),
  b: over.b * a + under.b * (1 - a),
  a: 1,
});

/** WCAG relative luminance. */
function lum(c) {
  const f = (v) => {
    const n = v / 255;
    return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
}

const contrast = (a, b) => {
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
};

const css = (c, a = 1) => `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${a})`;

const WHITE = { r: 255, g: 255, b: 255, a: 1 };
const BLACK = { r: 23, g: 23, b: 23, a: 1 };

/**
 * A colour that can be read on this one.
 * ---------------------------------------------------------------------------
 * The card used to be told which way its writing went - a boolean worked out
 * by the page from the palette's swatch. That was wrong whenever there was no
 * palette, which is the estate's own tints and therefore most of the time: the
 * page had nothing to measure, said nothing, and the face read it as "light"
 * and wrote everything in white. On a dark theme nobody noticed. On a light
 * one every card had white text on a nearly white ground.
 *
 * So nothing is told: the surface a card ACTUALLY ends up painted - its tint,
 * at its weight, over the page behind it - is measured here, and the writing
 * is whichever of near-white and near-black reads better on it.
 *
 * `want` is for the colours that are not the body text: a severity, the
 * accent. Those have a hue worth keeping, so rather than being replaced they
 * are walked towards whichever ink won until they clear the ratio asked for -
 * which keeps an orange orange and a red red while making both legible.
 */
function readable(want, surface, min = 3.2, ground = null) {
  const on = lum(surface);

  /* Which ink, with the page's own thumb on the scale.
     ---------------------------------------------------------------------
     Taking the better ratio alone is right at the ends and wrong in the
     middle: a mid-grey card reads about as well either way, the measurement
     comes out a hair in favour of white, and a light page ends up with two
     cards in white among forty in black. Which is worse than either choice
     made consistently.

     So the page's own ink wins ties, and only loses when the other is
     clearly - a third - better. The page's ink is read off the ground rather
     than passed in: a dark ground is a dark theme, and there is nothing else
     that needs to know. */
  const prefer = (ground === null ? on : lum(ground)) < 0.18 ? WHITE : BLACK;
  const other = prefer === WHITE ? BLACK : WHITE;
  const ink = contrast(lum(other), on) > contrast(lum(prefer), on) * 1.33 ? other : prefer;
  if (!want) return { ink, colour: ink };

  let c = want.a < 1 ? mix(want, surface, want.a) : want;
  for (let i = 0; i < 12 && contrast(lum(c), on) < min; i += 1) {
    c = mix(ink, c, 0.14);
  }
  return { ink, colour: c };
}

/* Tints resolved to what they actually paint as, kept until the theme moves.
   -------------------------------------------------------------------------
   A tint is whatever cardFill handed over, which is a hex when a palette is
   in use, `var(--wiki-tint-7)` when it is not, and a whole `color-mix(...)`
   expression when the cards are being painted solid. Only the browser knows
   what any of those come to, and asking it is a layout read - so each distinct
   expression is asked once and the answer kept. Ten tints and two variants of
   them is a dozen entries for a shelf of three hundred cards. */
const tints = new Map();

/** A tint - a swatch, a variable, or a mix of them - as a colour. */
export function resolveTint(tint) {
  const said = String(tint || "");
  if (!said) return "#2a2a2a";
  const had = tints.get(said);
  if (had) return had;

  const host = document.querySelector(".forge-wiki-stage")
    || document.querySelector(".forge-page")
    || document.body;
  const [got] = resolve(host, [said]);
  tints.set(said, got || "#2a2a2a");
  return tints.get(said);
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** As much of it as fits, with an ellipsis where it was cut - `text-overflow`. */
function fit(ctx, text, max) {
  const said = String(text ?? "");
  if (!said || ctx.measureText(said).width <= max) return said;
  let cut = said;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > max) cut = cut.slice(0, -1);
  return `${cut}…`;
}

/** `letter-spacing`, which canvas has no property for on older engines. */
function spaced(ctx, text, x, y, track) {
  if (!track) { ctx.fillText(text, x, y); return; }
  let at = x;
  for (const ch of String(text)) {
    ctx.fillText(ch, at, y);
    at += ctx.measureText(ch).width + track;
  }
}

const PAD = 14;
/* the card's own corner - see CARD_RADIUS */
const RADIUS = CARD_RADIUS;

/**
 * Draw one.
 *
 * @param canvas  the face, sized CARD_W*tex by CARD_H*tex
 * @param tile    { name, repo, goal, when, count, vulns, sev, docs }
 * @param look    { tint, opacity, light } - what cardLook worked out, with the
 *                tint already resolved to a colour and `light` saying which
 *                way the writing goes (what inkOn decides for the DOM card)
 * @param state   { lit, selected, sweep, tex } - lit is false for a card the
 *                search has passed over and null when nothing is being
 *                searched for; sweep is an angle in radians when the pointer
 *                is on this card, and nothing when it is not
 */
export function drawCardFace(canvas, tile, look, {
  lit = null, selected = false, sweep = null, full = false, tex = 2, glass = false, onReady,
} = {}) {
  const ctx = canvas.getContext("2d");
  const c = ink();
  const W = CARD_W;
  const H = CARD_H;

  ctx.save();
  ctx.setTransform(tex, 0, 0, tex, 0, 0);
  ctx.clearRect(0, 0, W, H);

  /* A card somebody has chosen goes solid, at full strength, whatever the
     fill setting says.

     Stepping back for a search is NOT done here any more. A card is a group
     of objects now - a body with sides, four boxes coming out of its face -
     and fading only the picture on the front left a ghost on a solid block.
     The group is faded instead, by whoever owns it; `lit` survives for the
     one thing that is genuinely about the drawing, which is the outline round
     a card the search found. */
  const back = 1;
  const weight = full ? 1 : look.opacity;

  /* The surface. In the DOM this is a ::before layer under the writing, so the
     text is never faded with it - here the two are drawn in order, which comes
     to the same thing.

     As glass it is the same tint at a third of that weight, with the streak
     off the top-left corner and the green at the foot over it: the same three
     layers the DOM card paints, in the same order (see "or a pane of glass"
     in ForgeWiki.css). What the stage is showing behind the card goes on
     showing through, which is the whole of the look. */
  ctx.globalAlpha = weight * back * (glass ? 0.3 : 1);
  ctx.fillStyle = look.tint;
  roundRect(ctx, 0.5, 0.5, W - 1, H - 1, RADIUS);
  ctx.fill();

  if (glass) {
    ctx.globalAlpha = back;

    const streak = ctx.createLinearGradient(0, 0, W * 0.62, H);
    streak.addColorStop(0, c.glassSheen);
    streak.addColorStop(0.34, "rgba(255,255,255,0)");
    ctx.fillStyle = streak;
    roundRect(ctx, 0.5, 0.5, W - 1, H - 1, RADIUS);
    ctx.fill();

    const floor = ctx.createLinearGradient(0, H, 0, H * 0.58);
    floor.addColorStop(0, c.glassFloor);
    floor.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = floor;
    roundRect(ctx, 0.5, 0.5, W - 1, H - 1, RADIUS);
    ctx.fill();
  }

  /* No edge at rest.
     -----------------------------------------------------------------------
     A hairline round every card is what the DOM one wears, and it is there
     because an element needs something to say where it ends. A quad does not:
     the tint already ends where the card does, and three hundred outlines
     drawn over each other on a sphere read as a wire frame rather than as a
     shelf. So the edge is spent on the two things it can actually say:

       the search found this one          an accent outline
       the pointer is on this one         a light running round it

     and a card that is neither simply has none. */
  ctx.globalAlpha = back;
  if (selected || lit) {
    ctx.lineWidth = selected ? 2 : 1.4;
    ctx.strokeStyle = c.accent;
    roundRect(ctx, 0.5, 0.5, W - 1, H - 1, RADIUS);
    ctx.stroke();
  } else if (glass) {
    /* ...except glass, which is nothing BUT its edge: with a third of a tint
       on the face, the rim is the only thing saying where the card is. The
       lip along the top is the light falling on it, and it is what stops a
       pane reading as a hole in the stage. */
    ctx.lineWidth = 1.2;
    ctx.strokeStyle = c.glassRim;
    roundRect(ctx, 0.6, 0.6, W - 1.2, H - 1.2, RADIUS);
    ctx.stroke();

    const lip = ctx.createLinearGradient(RADIUS, 0, W - RADIUS, 0);
    lip.addColorStop(0, "rgba(255,255,255,0)");
    lip.addColorStop(0.3, c.glassLip);
    lip.addColorStop(0.72, c.glassLip);
    lip.addColorStop(1, "rgba(255,255,255,0)");
    ctx.lineWidth = 1;
    ctx.strokeStyle = lip;
    ctx.beginPath();
    ctx.moveTo(RADIUS * 0.9, 1.1);
    ctx.lineTo(W - RADIUS * 0.9, 1.1);
    ctx.stroke();
  }

  /* The light that runs round a card under the pointer.
     -----------------------------------------------------------------------
     The DOM card does this with a conic gradient turned by an animated custom
     property, masked down to its own border (.forge-wiki-card::after). This
     is the same gradient, turned by the angle the caller passes - which is
     the clock - and stroked round the same rounded rectangle, which is the
     mask by another name. */
  if (sweep != null) {
    const light = ctx.createConicGradient
      ? ctx.createConicGradient(sweep, W / 2, H / 2)
      : null;
    if (light) {
      light.addColorStop(0, "rgba(255,255,255,0)");
      light.addColorStop(0.55, "rgba(255,255,255,0)");
      light.addColorStop(0.79, "rgba(255,255,255,0.35)");
      light.addColorStop(0.91, "rgba(255,255,255,0.95)");
      light.addColorStop(1, "rgba(255,255,255,0)");
      ctx.globalAlpha = 1;
      ctx.lineWidth = 2;
      ctx.strokeStyle = light;
      roundRect(ctx, 1, 1, W - 2, H - 2, RADIUS);
      ctx.stroke();
      ctx.globalAlpha = back;
    }
  }

  /* Which way the writing goes on this surface - measured, not assumed: the
     tint at its weight over the page behind it is what a reader actually
     sees, so that is what the ink is chosen against. See readable(). */
  const painted = rgb(look.tint) || BLACK;
  const under = rgb(c.ground) || BLACK;
  /* ...and as glass that is a third of the tint over the ground, not the
     whole of it: the ink has to be chosen against what was actually painted,
     or a pane gets the ink for a card three times more solid than it is. */
  const laid = weight * (glass ? 0.3 : 1) * (painted.a ?? 1);
  const surface = mix(painted, under, Math.max(0, Math.min(1, laid)));

  /* not `ink` - that is the token cache this function already called */
  const pen = readable(null, surface, 3.2, under).ink;
  const on = css(pen, 0.94);
  const dim = css(pen, 0.48);
  /* the accent keeps its hue and is walked towards the ink until it reads */
  const accent = css(readable(rgb(c.accent), surface, 3.4, under).colour, 1);

  ctx.textBaseline = "top";
  ctx.textAlign = "left";

  /* The band across the top, for a card painted in a severity. */
  let top = PAD;
  if (tile.sev) {
    ctx.globalAlpha = back;
    ctx.font = `600 9px ${c.mono}`;
    ctx.fillStyle = on;
    spaced(ctx, String(tile.sev.key).toUpperCase(), PAD, 6, 0.8);
    ctx.textAlign = "right";
    ctx.fillText(String(tile.sev.n), W - PAD, 6);
    ctx.textAlign = "left";
    top = PAD + 8;
  }

  /* The head: the mark, the name, and the repository or address under it. The
     room the writing has is the card minus its padding, minus the mark on the
     left and whatever sits on the right. */
  const counted = tile.vulns > 0 ? `${tile.vulns}` : "";
  const countW = counted ? 34 : 0;

  const mark = markFor(tile.icon, onReady);
  /* 16 and a 10 gap, which is .forge-wiki-card__head's own measure - held
     even when the mark has not arrived, so the writing does not jump when it
     does */
  const markW = tile.icon ? 26 : 0;
  if (mark) {
    ctx.globalAlpha = back;
    ctx.drawImage(mark, PAD, top + 1, 16, 16);
  }

  const left = PAD + markW;
  const room = W - PAD - left - countW;

  ctx.globalAlpha = back;
  ctx.font = `500 14px ${c.display}`;
  ctx.fillStyle = on;
  ctx.fillText(fit(ctx, tile.name, room), left, top);

  if (tile.repo) {
    ctx.font = `10.5px ${c.mono}`;
    ctx.fillStyle = dim;
    ctx.fillText(fit(ctx, tile.repo, room), left, top + 19);
  }

  /* What was found on it, in the corner - on the card at rest, as it is in the
     DOM: a number you have to hover for is a number you cannot scan a shelf
     for. The shield is the same path the card draws. */
  if (counted) {
    ctx.globalAlpha = back;
    ctx.strokeStyle = on;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    const sx = W - PAD - 30;
    const sy = top + 1;
    ctx.moveTo(sx + 5, sy);
    ctx.lineTo(sx + 10, sy + 2);
    ctx.lineTo(sx + 10, sy + 6.5);
    ctx.quadraticCurveTo(sx + 10, sy + 10.5, sx + 5, sy + 12.5);
    ctx.quadraticCurveTo(sx, sy + 10.5, sx, sy + 6.5);
    ctx.lineTo(sx, sy + 2);
    ctx.closePath();
    ctx.stroke();

    ctx.font = `600 11px ${c.mono}`;
    ctx.fillStyle = on;
    ctx.fillText(counted, sx + 14, top + 1);
  }

  /* The severities, along the bottom - the ones there are.
     -------------------------------------------------------------------------
     What a machine or an application is carrying is the question these two
     pages are open to answer, and a number you have to hover for or turn a
     lens on for is a number you cannot scan a shelf for. So they are on every
     card, in their own colours, each in its own place - which is what makes a
     shelf of them readable at a glance: the eye learns where "high" is and
     then only has to look at that column.

     A zero is not drawn at all. The slots are fixed, so leaving one out never
     moves the others - what is on a card is only ever what was found. */
  const strip = H - PAD - 9;
  if (tile.sevs) {
    const span = (W - PAD * 2) / SEVERITIES.length;
    SEVERITIES.forEach((key, i) => {
      const n = Number(tile.sevs[key]) || 0;
      if (!n) return;
      const at = PAD + i * span;

      ctx.globalAlpha = back;
      ctx.fillStyle = css(readable(rgb(c.sevs[key]), surface, 3, under).colour, 1);
      roundRect(ctx, at, strip + 1.5, 6, 6, 1.5);
      ctx.fill();

      ctx.font = `600 10px ${c.mono}`;
      ctx.fillStyle = on;
      ctx.fillText(n > 999 ? "999+" : String(n), at + 10, strip);
    });
    ctx.globalAlpha = back;
  }

  /* ...and the line above it: what it is, and when it was last seen. */
  const base = tile.sevs ? strip - 15 : H - PAD - 11;
  ctx.font = `10px ${c.mono}`;
  ctx.fillStyle = accent;
  const goal = String(tile.goal || "").toUpperCase();
  spaced(ctx, fit(ctx, goal, W - PAD * 2 - 70), PAD, base, 1.2);

  if (tile.when) {
    ctx.font = `10.5px ${c.mono}`;
    ctx.fillStyle = dim;
    ctx.textAlign = "right";
    ctx.fillText(fit(ctx, tile.when, 70), W - PAD, base);
    ctx.textAlign = "left";
  }

  /* How many documents it has, where the card puts its own count. */
  if (tile.docs > 0) {
    ctx.font = `600 9.5px ${c.mono}`;
    ctx.fillStyle = dim;
    ctx.textAlign = "right";
    ctx.fillText(`${tile.docs} doc${tile.docs === 1 ? "" : "s"}`, W - PAD, base - 14);
    ctx.textAlign = "left";
  }

  ctx.restore();
}

export { CARD_W, CARD_H, CARD_RADIUS };
